import { Pto } from '@outcoreaix/pto'
import querystringify from 'querystringify'

import { Button, Flex, Spin, Typography } from 'antd'
import { useCheckMembershipQuery, useGetSpacesQuery } from 'api/spaces-api'
import { useGetWorkspaceQuery } from 'api/workspaces-api'
import { AppRoutes } from 'app/app-routes'
import { authStorage } from 'app/auth-storage'
import { useTheme } from 'components/ThemeContext'
import AddNameDetails from 'features/users/components/AddNameDetails'
import useOnboardingFlow from 'hooks/useOnboarding'
import useStaffboarding from 'hooks/useStaffOnboarding'
import { useEffect, useState } from 'react'
import { Link, Navigate, Outlet, useLocation, useOutletContext, useParams } from 'react-router-dom'

type Props = {
  roleRequired?: (Pto.Users.UserRole | Pto.Workspaces.WorkspaceRole)[]
}

const UserAccessRoute = ({ roleRequired = [] }: Props) => {
  const { roleMap } = useOutletContext<{
    roleMap: Record<number, Pto.Users.UserRole | Pto.Workspaces.WorkspaceRole> | null
  }>()

  const params = useParams()
  const location = useLocation()
  const user = authStorage.user
  const currentWorkspace = params.workspaceId
  const currentWorkspaceRole = currentWorkspace ? roleMap?.[Number(currentWorkspace)] : undefined

  const { data: memberData, isLoading } = useCheckMembershipQuery(
    { workspaceId: Number(currentWorkspace) },
    {
      skip: currentWorkspace === undefined || currentWorkspaceRole !== Pto.Workspaces.WorkspaceRole.Contact
    }
  )

  const { isLoading: useOnboardingLoading, onboardingNeeded } = useOnboardingFlow(
    Number(params.spaceId),
    currentWorkspaceRole !== Pto.Workspaces.WorkspaceRole.Contact
  )

  const { onboardingNeeded: staffOnbooardingNeeded } = useStaffboarding(
    Number(params.workspaceId),
    Number(params.spaceId)
  )

  // Learners are asked for their name inside their own onboarding flow, which staff and
  // admins have no route to, so a generated placeholder name used to stick with them for
  // good. Same condition as hooks/useOnboarding. nameSubmitted re-renders us once the
  // name is saved, since authStorage on its own does not.
  const [nameSubmitted, setNameSubmitted] = useState(false)
  const nameDetailsNeeded =
    !nameSubmitted &&
    (currentWorkspaceRole === Pto.Workspaces.WorkspaceRole.Staff ||
      currentWorkspaceRole === Pto.Workspaces.WorkspaceRole.Admin) &&
    (authStorage.getPendingUserNameSet() || authStorage.userNameSetNeeded())

  const {
    data: spacesData,
    isLoading: spacesLoading,
    isUninitialized,
    refetch
  } = useGetSpacesQuery(
    {
      workspaceId: Number(currentWorkspace)
    },
    { skip: currentWorkspace === undefined }
  )

  const { data: workspace, isLoading: isWorkspaceLoading } = useGetWorkspaceQuery(Number(currentWorkspace) || 0, {
    skip: !currentWorkspace
  })
  const { setThemeColors } = useTheme()

  useEffect(() => {
    if (workspace) {
      setThemeColors(workspace.colors || { primaryColor: '', secondaryColor: '' })
    }
  }, [isWorkspaceLoading])

  useEffect(() => {
    if (!isUninitialized && currentWorkspace !== undefined) {
      refetch()
    }
  }, [user, refetch, isUninitialized])

  if (
    (!currentWorkspaceRole && currentWorkspace) ||
    (!currentWorkspaceRole &&
      roleRequired.includes(Pto.Users.UserRole.SystemAdmin) &&
      !user?.roles.includes(Pto.Users.UserRole.SystemAdmin))
  ) {
    return <Navigate to={AppRoutes.main} />
  }

  if (currentWorkspaceRole && !roleRequired.includes(currentWorkspaceRole)) {
    return (
      <Flex vertical className="w-full h-dvh lg:h-screen" justify="center" align="center" gap={16}>
        <Flex>
          <Typography.Title level={2}>Access denied!</Typography.Title>
        </Flex>
        <Link to={AppRoutes.main}>
          <Button
            type="primary"
            size="large"
            onClick={() => localStorage.removeItem(`${authStorage.user?.id}-workspace`)}
          >
            Go to the main page
          </Button>
        </Link>
      </Flex>
    )
  }

  if (isLoading) {
    return (
      <Flex justify="center" align="center" className="w-full h-dvh lg:h-screen">
        <Spin spinning size="large" />
      </Flex>
    )
  }

  localStorage.setItem(`${user?.id}-workspace`, `${currentWorkspace}`)

  if (
    memberData &&
    currentWorkspaceRole === Pto.Workspaces.WorkspaceRole.Contact &&
    !useOnboardingLoading &&
    onboardingNeeded &&
    location.pathname !== `/workspaces/${memberData.workspaceId}/space/${memberData.id}${AppRoutes.learnerOnboarding}`
  ) {
    return (
      <Navigate to={`/workspaces/${memberData.workspaceId}/space/${memberData.id}${AppRoutes.learnerOnboarding}`} />
    )
  } else if (nameDetailsNeeded) {
    return <AddNameDetails blobAvatarUrl="/alex-big.png" onFinish={() => setNameSubmitted(true)} />
  } else if (staffOnbooardingNeeded && !location.pathname.includes(`${AppRoutes.cpdNotice}`)) {
    const callbackUrl = querystringify.stringify({
      [Pto.App.ParamKeyEnum.CallbackUrl]: location.pathname + location.search
    })
    return <Navigate to={`${AppRoutes.workspaces}/${currentWorkspace}${AppRoutes.cpdNotice}?${callbackUrl}`} />
  }

  return (
    <Outlet
      context={{ isLoading: spacesLoading, spaces: spacesData?.items || [], currentWorkspace, currentWorkspaceRole }}
    />
  )
}

export default UserAccessRoute
